<script>
import firebase from "firebase/app";
import "firebase/firestore";

import Portal from "../../layouts/portal";
import GestionMembresia from "@/components/portal-membresia-gestion";
import { clienteComputed, negocioComputed } from "@/state/helpers";
import { etiquetaTipo } from "@/services/usuarios";
import { TERMINOS, urlDelBrochure } from "@/services/documentos";
import { cargarReferidos, resumenReferidos, COMISION_POR_REFERIDO } from "@/services/referidos";
import { DOCUMENTOS, subirDocumento, guardarRfc } from "@/services/documentosPersonales";

/**
 * Network: el centro del perfil, equivalente a la pestaña del mismo nombre en
 * la app. Reúne datos, membresía, referidos, la administración del negocio
 * (para comercios) y los documentos.
 *
 * Los datos y la membresía son de consulta/gestión; editar el perfil sigue en
 * la app, donde está el formulario con validaciones y carga de documentos.
 */
export default {
  page: { title: "Network" },
  components: { Portal, GestionMembresia },
  data() {
    return {
      copiado: false,
      terminos: TERMINOS,
      abriendoBrochure: false,
      // Accesos a la administración del negocio; sólo para comercios.
      accesosNegocio: [
        { nombre: "portal-empresa", texto: "Mi empresa", icono: "mdi mdi-store-cog",
          desc: "Tus datos, promoción y ubicación." },
        { nombre: "portal-mis-eventos", texto: "Mis eventos", icono: "mdi mdi-calendar-edit",
          desc: "Publica en Qué plan hoy." },
        { nombre: "portal-mis-vacantes", texto: "Mis vacantes", icono: "mdi mdi-briefcase-edit",
          desc: "Ofrece empleo y revisa postulaciones." },
      ],

      // Mis referidos
      mostrarReferidos: false,
      cargandoReferidos: false,
      referidos: [],
      comision: COMISION_POR_REFERIDO,

      // Mis documentos (datos bancarios + documentos)
      mostrarDocumentos: false,
      guardandoBanco: false,
      bancoError: null,
      banco: "",
      clabe: "",
      rfc: "",
      documentos: DOCUMENTOS,
      subiendo: {},          // { [campo]: true } mientras sube ese documento
      docError: null,
      camposReferidos: [
        { key: "name", label: "Nombre" },
        { key: "estado", label: "Estado" },
        { key: "renovacionTexto", label: "Renueva" },
      ],
    };
  },
  computed: {
    ...clienteComputed,
    ...negocioComputed,
    tipo() {
      return this.perfilCliente ? etiquetaTipo(this.perfilCliente.tipo) : "—";
    },
    resumen() {
      return resumenReferidos(this.referidos);
    },
    clabeValida() {
      // La CLABE mexicana son 18 dígitos. Se avisa, no se bloquea (la app
      // tampoco valida el largo).
      return /^\d{18}$/.test(this.clabe.trim());
    },
  },
  methods: {
    formatoMoneda(valor) {
      return new Intl.NumberFormat("es-MX", {
        style: "currency", currency: "MXN", maximumFractionDigits: 0,
      }).format(valor || 0);
    },

    async abrirReferidos() {
      this.mostrarReferidos = true;
      // Se carga al abrir, no al montar Network, para no gastar peticiones si
      // el usuario nunca consulta sus referidos.
      if (this.referidos.length || this.cargandoReferidos) return;

      const codigo = this.perfilCliente && this.perfilCliente.referral_code;
      if (!codigo) return;

      this.cargandoReferidos = true;
      try {
        this.referidos = await cargarReferidos(codigo);
      } catch (err) {
        console.error("[portal] error al cargar referidos:", err);
      } finally {
        this.cargandoReferidos = false;
      }
    },

    abrirDocumentos() {
      const p = this.perfilCliente || {};
      this.banco = p.banco || "";
      this.clabe = p.clabe || "";
      this.rfc = p.rfc || "";
      this.bancoError = null;
      this.docError = null;
      this.mostrarDocumentos = true;
    },

    /** Guarda banco, CLABE y RFC (los datos de texto). */
    async guardarBanco() {
      if (!this.banco.trim() || !this.clabe.trim()) {
        this.bancoError = "Escribe el nombre del banco y la CLABE.";
        return;
      }
      const id = this.perfilCliente && this.perfilCliente.id;
      if (!id) {
        this.bancoError = "No pudimos identificar tu cuenta.";
        return;
      }

      this.guardandoBanco = true;
      this.bancoError = null;
      try {
        await firebase.firestore().collection("app_usuarios").doc(id).update({
          banco: this.banco.trim(),
          clabe: this.clabe.trim(),
        });
        if (this.rfc.trim()) await guardarRfc(id, this.rfc);
        await this.$store.dispatch("cliente/cargar", { force: true });
        this.mostrarDocumentos = false;
      } catch (err) {
        console.error("[portal] error al guardar datos bancarios:", err);
        this.bancoError = "No pudimos guardar tus datos. Inténtalo de nuevo.";
      } finally {
        this.guardandoBanco = false;
      }
    },

    /** URL ya subida de un documento, si existe. */
    urlDocumento(campo) {
      return this.perfilCliente ? this.perfilCliente[campo] : null;
    },

    /**
     * Sube un documento en cuanto se elige el archivo (como la app), y refresca
     * el perfil para que aparezca el enlace "Ver documento".
     */
    async onSubirDocumento(doc, evento) {
      const archivo = evento.target.files && evento.target.files[0];
      if (!archivo) return;

      const id = this.perfilCliente && this.perfilCliente.id;
      const email = this.perfilCliente && this.perfilCliente.email;
      if (!id || !email) {
        this.docError = "No pudimos identificar tu cuenta.";
        return;
      }

      this.$set(this.subiendo, doc.campo, true);
      this.docError = null;
      try {
        await subirDocumento({ email, id, tipo: doc.tipo, campo: doc.campo, archivo });
        await this.$store.dispatch("cliente/cargar", { force: true });
      } catch (err) {
        console.error("[portal] error al subir documento:", err);
        this.docError = `No pudimos subir ${doc.label}. Inténtalo de nuevo.`;
      } finally {
        this.$set(this.subiendo, doc.campo, false);
        // Permite volver a elegir el mismo archivo si hizo falta reintentar.
        evento.target.value = "";
      }
    },

    async abrirBrochure() {
      this.abriendoBrochure = true;
      try {
        const url = await urlDelBrochure();
        window.open(url, "_blank", "noopener");
      } catch (err) {
        console.error("[portal] no se pudo abrir el brochure:", err);
        this.$bvToast && this.$bvToast.toast
          ? this.$bvToast.toast("No pudimos abrir el brochure. Inténtalo de nuevo.", {
              title: "Brochure", variant: "warning", solid: true,
            })
          : alert("No pudimos abrir el brochure. Inténtalo de nuevo.");
      } finally {
        this.abriendoBrochure = false;
      }
    },
    async copiarCodigo() {
      const codigo = this.perfilCliente && this.perfilCliente.referral_code;
      if (!codigo) return;

      try {
        await navigator.clipboard.writeText(codigo);
        this.copiado = true;
        setTimeout(() => { this.copiado = false; }, 2000);
      } catch (err) {
        // Navegadores sin permiso de portapapeles: el código está a la vista,
        // se puede copiar a mano.
        console.warn("[portal] no se pudo copiar el código:", err);
      }
    },
    async actualizarMembresia() {
      await this.$store.dispatch("cliente/cargarMembresia");
    },
  },
  mounted() {
    // Se necesita saber si el usuario ya tiene negocio para decidir entre las
    // tarjetas y el botón de "Registrar mi empresa".
    this.$store.dispatch("negocio/cargar");
  },
};
</script>

<template>
  <Portal>
    <h1 class="h3 mb-4">Network</h1>

    <!-- Completa tu registro: como en la app, es lo primero que ve quien se
         acaba de registrar. -->
    <div v-if="perfilIncompleto" class="completar-aviso mb-4">
      <div class="d-flex align-items-start">
        <i class="mdi mdi-alert-circle completar-icono"></i>
        <div>
          <h2 class="h5 mb-1">Completa tu registro</h2>
          <p class="text-muted mb-3">
            Aún tienes datos pendientes. Complétalo para usar todas las funciones.
          </p>
          <b-button variant="primary" :to="{ name: 'portal-completar' }">
            <i class="mdi mdi-pencil"></i> Ir a completar registro
          </b-button>
        </div>
      </div>
    </div>

    <b-row>
      <b-col lg="6" class="mb-4">
        <div class="cuenta-card">
          <h2 class="h5 mb-3">Mis datos</h2>
          <div v-if="perfilCliente">
            <p class="mb-2"><strong>Nombre:</strong> {{ perfilCliente.name || '—' }}</p>
            <p class="mb-2"><strong>Correo:</strong> {{ perfilCliente.email }}</p>
            <p class="mb-2"><strong>Teléfono:</strong> {{ perfilCliente.phone || '—' }}</p>
            <p class="mb-2"><strong>Ciudad:</strong> {{ perfilCliente.ciudad }}</p>
            <p class="mb-2"><strong>Tipo:</strong> {{ tipo }}</p>
            <p class="mb-0"><strong>Miembro desde:</strong> {{ perfilCliente.creadoEnFormatted }}</p>
          </div>
          <p v-else class="text-muted mb-0">Cargando tu perfil…</p>

        </div>
      </b-col>

      <b-col lg="6" class="mb-4">
        <div class="cuenta-card">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <h2 class="h5 mb-0">Mi membresía</h2>
            <b-button size="sm" variant="outline-secondary" @click="actualizarMembresia">
              <i class="mdi mdi-refresh"></i>
            </b-button>
          </div>

          <div v-if="estadoMembresia">
            <b-badge :variant="estadoMembresia.variante" class="mb-3">
              {{ estadoMembresia.etiqueta }}
            </b-badge>

            <p v-if="tieneMembresia" class="mb-0">
              <strong>Vigente hasta:</strong> {{ estadoMembresia.vigencia }}
              <span v-if="estadoMembresia.diasRestantes !== null" class="text-muted">
                ({{ estadoMembresia.diasRestantes }} días)
              </span>
            </p>
          </div>
          <p v-else class="text-muted mb-0">Consultando tu membresía…</p>

          <hr />
          <GestionMembresia />
        </div>
      </b-col>
    </b-row>

    <div v-if="perfilCliente && perfilCliente.referral_code" class="cuenta-card mb-4">
      <h2 class="h5 mb-2">Mis referidos</h2>
      <p class="text-muted">
        Comparte tu código: cuando alguien se registre con él, queda ligado a ti y ganas
        {{ formatoMoneda(comision) }} al mes por cada uno con membresía activa.
      </p>
      <div class="codigo-caja mb-3">
        <span class="codigo">{{ perfilCliente.referral_code }}</span>
        <b-button size="sm" :variant="copiado ? 'success' : 'outline-primary'" @click="copiarCodigo">
          <i class="mdi" :class="copiado ? 'mdi-check' : 'mdi-content-copy'"></i>
          {{ copiado ? 'Copiado' : 'Copiar' }}
        </b-button>
      </div>
      <div class="d-flex flex-wrap">
        <b-button variant="primary" class="mr-2 mb-2" @click="abrirReferidos">
          <i class="mdi mdi-account-group"></i> Ver mis referidos
        </b-button>
        <b-button variant="outline-primary" class="mb-2" @click="abrirDocumentos">
          <i class="mdi mdi-file-account"></i> Mis documentos
        </b-button>
      </div>
    </div>

    <!-- Modal: Mis referidos -->
    <b-modal v-model="mostrarReferidos" size="lg" centered hide-footer title="Mis referidos">
      <p v-if="cargandoReferidos" class="text-muted">Cargando tus referidos…</p>

      <template v-else>
        <b-row class="text-center mb-4">
          <b-col cols="6" md="3" class="mb-3">
            <div class="ref-num">{{ resumen.total }}</div>
            <div class="text-muted small">Total</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-3">
            <div class="ref-num text-success">{{ resumen.conMembresia }}</div>
            <div class="text-muted small">Con membresía</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-3">
            <div class="ref-num text-muted">{{ resumen.sinMembresia }}</div>
            <div class="text-muted small">Sin membresía</div>
          </b-col>
          <b-col cols="6" md="3" class="mb-3">
            <div class="ref-num text-primary">{{ formatoMoneda(resumen.dineroEsteMes) }}</div>
            <div class="text-muted small">Este mes</div>
          </b-col>
        </b-row>

        <p class="text-muted small">
          Cada referido con membresía activa aporta {{ formatoMoneda(comision) }} en el mes que le
          toca renovar. Para el próximo mes van {{ formatoMoneda(resumen.dineroProximoMes) }}.
        </p>

        <p v-if="referidos.length === 0" class="text-muted mb-0">
          Todavía nadie se ha registrado con tu código.
        </p>

        <b-table v-else small responsive hover :items="referidos" :fields="camposReferidos">
          <template #cell(name)="{ item }">
            {{ item.name }}<br />
            <small class="text-muted">{{ item.email }}</small>
          </template>
          <template #cell(estado)="{ item }">
            <b-badge :variant="item.activo ? 'success' : 'secondary'">
              {{ item.activo ? 'Con membresía' : 'Sin membresía' }}
            </b-badge>
          </template>
          <template #cell(renovacionTexto)="{ item }">
            <span v-if="item.activo">
              {{ item.renovacionTexto }}
              <b-badge v-if="item.cuentaEsteMes" variant="primary" class="ml-1">Este mes</b-badge>
            </span>
            <span v-else class="text-muted">—</span>
          </template>
        </b-table>
      </template>
    </b-modal>

    <!-- Modal: Mis documentos (datos bancarios + documentos) -->
    <b-modal v-model="mostrarDocumentos" centered title="Mis documentos" hide-footer>
      <h3 class="h6 mb-1">Datos bancarios</h3>
      <p class="text-muted small">Aquí depositamos tus comisiones por referidos.</p>

      <b-alert :show="!!bancoError" variant="danger" class="py-2 small">{{ bancoError }}</b-alert>

      <b-form-group label="Nombre del banco">
        <b-form-input v-model="banco" placeholder="Ej. BBVA, Banorte…" />
      </b-form-group>

      <b-form-group label="CLABE">
        <b-form-input v-model="clabe" placeholder="18 dígitos" maxlength="18" />
        <small v-if="clabe && !clabeValida" class="text-warning">
          La CLABE suele tener 18 dígitos; revisa que esté completa.
        </small>
      </b-form-group>

      <b-form-group label="RFC">
        <b-form-input v-model="rfc" placeholder="Tu RFC" />
      </b-form-group>

      <div class="d-flex justify-content-end mb-4">
        <b-button variant="secondary" class="mr-2" @click="mostrarDocumentos = false">Cancelar</b-button>
        <b-button variant="primary" :disabled="guardandoBanco" @click="guardarBanco">
          {{ guardandoBanco ? 'Guardando…' : 'Guardar datos' }}
        </b-button>
      </div>

      <hr />

      <h3 class="h6 mb-1">Documentos</h3>
      <p class="text-muted small">Se guardan al elegir el archivo.</p>

      <b-alert :show="!!docError" variant="danger" class="py-2 small">{{ docError }}</b-alert>

      <div v-for="doc in documentos" :key="doc.campo" class="doc-fila">
        <div class="flex-grow-1">
          <div class="font-weight-bold">{{ doc.label }}</div>
          <a v-if="urlDocumento(doc.campo)" :href="urlDocumento(doc.campo)" target="_blank"
            rel="noopener noreferrer" class="small text-success">
            <i class="mdi mdi-check-circle"></i> Ver documento
          </a>
          <span v-else class="small text-muted">Sin subir</span>
        </div>
        <div>
          <label class="btn btn-outline-primary btn-sm mb-0" :class="{ disabled: subiendo[doc.campo] }">
            {{ subiendo[doc.campo] ? 'Subiendo…' : (urlDocumento(doc.campo) ? 'Reemplazar' : 'Subir') }}
            <input type="file" class="d-none" :disabled="subiendo[doc.campo]"
              accept="image/*,application/pdf" @change="onSubirDocumento(doc, $event)" />
          </label>
        </div>
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button variant="secondary" @click="mostrarDocumentos = false">Cerrar</b-button>
      </div>
    </b-modal>

    <!-- Mi negocio. Las tarjetas aparecen sólo cuando ya hay un negocio
         registrado; si no, se ofrece registrarlo (a comercios y a usuarios
         normales, que al registrar quedan activados como comercio). -->
    <div class="cuenta-card mb-4">
      <h2 class="h5 mb-3">Mi negocio</h2>

      <b-row v-if="tieneNegocio">
        <b-col v-for="a in accesosNegocio" :key="a.nombre" md="4" class="mb-3 mb-md-0">
          <router-link :to="{ name: a.nombre }" class="acceso">
            <i :class="a.icono" class="acceso-icono"></i>
            <div>
              <div class="acceso-titulo">{{ a.texto }}</div>
              <div class="text-muted small">{{ a.desc }}</div>
            </div>
          </router-link>
        </b-col>
      </b-row>

      <div v-else-if="!cargandoNegocio" class="registrar-negocio">
        <p class="text-muted mb-3">
          ¿Tienes un negocio? Regístralo para aparecer en el directorio de Recnet y poder
          publicar eventos y vacantes.
        </p>
        <b-button variant="success" :to="{ name: 'portal-empresa' }">
          <i class="mdi mdi-store-plus"></i> Registrar mi empresa
        </b-button>
      </div>

      <p v-else class="text-muted mb-0">Cargando…</p>
    </div>

    <!-- Documentos -->
    <div class="cuenta-card">
      <h2 class="h5 mb-3">Documentos</h2>
      <ul class="documentos">
        <li>
          <a href="#" @click.prevent="abrirBrochure">
            <i class="mdi mdi-file-pdf-box doc-icono"></i>
            <span>
              <span class="doc-titulo">Brochure RecNet</span>
              <span class="text-muted small d-block">
                {{ abriendoBrochure ? 'Abriendo…' : 'Documento informativo' }}
              </span>
            </span>
            <i class="mdi mdi-chevron-right doc-flecha"></i>
          </a>
        </li>
        <li v-for="t in terminos" :key="t.subtitulo">
          <a :href="t.url" target="_blank" rel="noopener noreferrer">
            <i class="mdi mdi-file-document-outline doc-icono"></i>
            <span>
              <span class="doc-titulo">{{ t.titulo }}</span>
              <span class="text-muted small d-block">{{ t.subtitulo }}</span>
            </span>
            <i class="mdi mdi-chevron-right doc-flecha"></i>
          </a>
        </li>
      </ul>
    </div>
  </Portal>
</template>

<style scoped>
.completar-aviso {
  background: #fff;
  border: 1px solid #cdb9ec;
  border-left: 4px solid #4b2e83;
  border-radius: 12px;
  padding: 20px 24px;
}

.completar-icono {
  font-size: 32px;
  color: #f1734f;
  margin-right: 14px;
  flex-shrink: 0;
}

.cuenta-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  height: 100%;
}

.codigo-caja {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
}

.codigo {
  font-family: monospace;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 1px;
  color: #4b2e83;
}

.ref-num {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
}

.doc-fila {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f1f4;
}

.doc-fila:last-of-type {
  border-bottom: 0;
}

.acceso {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  color: inherit;
  height: 100%;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.acceso:hover {
  text-decoration: none;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.acceso-icono {
  font-size: 24px;
  color: #4b2e83;
  flex-shrink: 0;
}

.acceso-titulo {
  font-weight: 600;
}

.documentos {
  list-style: none;
  padding: 0;
  margin: 0;
}

.documentos a {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  color: inherit;
  border-bottom: 1px solid #f1f1f4;
}

.documentos li:last-child a {
  border-bottom: 0;
}

.documentos a:hover {
  text-decoration: none;
  color: #4b2e83;
}

.doc-icono {
  font-size: 24px;
  color: #4b2e83;
  flex-shrink: 0;
}

.doc-titulo {
  font-weight: 600;
}

.doc-flecha {
  margin-left: auto;
  color: #adb5bd;
}
</style>
